import React, { forwardRef, useRef } from 'react';
import styles from './FakeSelectionButton.module.scss';

// FakeSelectionButton is a button that contains a fake selection container.
// It makes sure that every time the button is clicked, the native selection
// will be moved into the fake selection container.
//
// # Purpose
// The purpose of this component is to make sure the event target of clipboard
// events are always the fake selection container, so that the events can be
// captured by the table container.
//
// # Background
// The idea is pretty similar to `embedSelectionContainer` used in QuillComponent,
// except that we are moving the selection container inside the button.
// The cause is Blink considers the selection is invisible when it and the focused
// element (`document.activeElement`) are in the different subtrees.
// If the selection is invisible, Blink will set the event target to body,
// which won't be able to be captured by the table container.
// Related code:
// https://github.com/chromium/chromium/blob/94.0.4603.1/third_party/blink/renderer/core/editing/commands/clipboard_commands.cc#L97-L99
const FakeSelectionButton = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
  // eslint-disable-next-line react/prop-types
>(({ onClick, children, ...buttonProps }, ref) => {
  const fakeSelectionContainerRef = useRef<HTMLDivElement>(null);

  return (
    <button
      ref={ref}
      type="button"
      {...buttonProps}
      onClick={(event) => {
        onClick?.(event);

        if (fakeSelectionContainerRef.current) {
          const selection = document.getSelection();
          selection?.removeAllRanges();
          const range = document.createRange();
          range.setStart(fakeSelectionContainerRef.current, 0);
          selection?.addRange(range);
        }
      }}
    >
      <span ref={fakeSelectionContainerRef} className={styles.fakeSelection}>
        {/*
          When the node is empty, Blink canonicalizes the selection, and will
          move the selection to the first non-empty subsequent node, which means
          the selection will be moved out of FakeSelectionButton. When this happens,
          the focused element and the selection belong to the different subtrees, so
          the event target of the clipboard events will be set to body.
        */}
        Table Selection
      </span>
      {children}
    </button>
  );
});

export default FakeSelectionButton;
